/**
 * Auth Store
 * Manages user authentication state and registration/approval state
 */

import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type { BankAccount, LoginCredentials, Pharmacy, RegisterData, User } from '@/types'
import { authService } from '@/services/api'
import { DEFAULT_LOCALE, setLocale, t } from '@/i18n'

/**
 * The persisted profile language is the authoritative UI locale for signed-in
 * users (PSP-355): it wins over whatever localStorage guessed pre-auth, so the
 * preference follows the user across devices. Users without a stored
 * preference fall back to Spanish.
 */
function applyUserLanguage(user: User): void {
  setLocale(user.language ?? DEFAULT_LOCALE)
}

interface RegisterResult {
  status: 'authenticated' | 'pending_approval'
  message?: string
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const pharmacy = ref<Pharmacy | null>(null)
  const bankAccount = ref<BankAccount | null>(null)
  const token = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const isInitialized = ref(false)

  const pendingRegistration = ref<{ email: string; message: string } | null>(null)

  const isAuthenticated = computed(() => !!user.value && !!token.value)
  const isOwner = computed(() => user.value?.role === 'owner')
  const isPharmacist = computed(() => user.value?.role === 'pharmacist')
  const isCampaignAdmin = computed(() => (user.value?.globalRoles ?? []).includes('campaign_admin'))
  const isReviewer = computed(() => (user.value?.globalRoles ?? []).includes('reviewer'))
  const isReviewerL2 = computed(() => (user.value?.globalRoles ?? []).includes('reviewer_l2'))
  const userFullName = computed(() => user.value ? `${user.value.name} ${user.value.surname}`.trim() : '')

  async function initialize() {
    if (isInitialized.value) return

    isLoading.value = true
    error.value = null

    try {
      const currentUser = await authService.getCurrentUser()
      if (!currentUser) return

      user.value = currentUser
      token.value = authService.getToken()
      applyUserLanguage(currentUser)

      if (currentUser.pharmacyId) {
        pharmacy.value = await authService.getPharmacy(currentUser.pharmacyId)
      }
    } catch (e) {
      console.error('Auth initialization failed:', e)
    } finally {
      isLoading.value = false
      isInitialized.value = true
    }
  }

  async function login(credentials: LoginCredentials) {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.login(credentials)
      user.value = response.user
      token.value = response.token
      pendingRegistration.value = null
      applyUserLanguage(response.user)

      if (response.user.pharmacyId) {
        pharmacy.value = await authService.getPharmacy(response.user.pharmacyId)
      }

      return response.user
    } catch (e) {
      error.value = e instanceof Error ? e.message : t('errors.login')
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function register(data: RegisterData): Promise<RegisterResult> {
    isLoading.value = true
    error.value = null

    try {
      const role = data.role ?? 'employee'

      if (role === 'owner') {
        const response = await authService.registerOwner(data)
        user.value = response.user
        token.value = response.token
        pendingRegistration.value = null
        applyUserLanguage(response.user)

        if (response.user.pharmacyId) {
          pharmacy.value = await authService.getPharmacy(response.user.pharmacyId)
        }

        return { status: 'authenticated' }
      }

      const pending = await authService.registerEmployee(data)
      pendingRegistration.value = {
        email: data.email,
        message: pending.message,
      }

      return {
        status: 'pending_approval',
        message: pending.message,
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : t('errors.register')
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function logout() {
    isLoading.value = true

    try {
      await authService.logout()
    } finally {
      user.value = null
      pharmacy.value = null
      bankAccount.value = null
      token.value = null
      error.value = null
      pendingRegistration.value = null
      isLoading.value = false
    }
  }

  async function requestPasswordReset(email: string) {
    isLoading.value = true
    error.value = null

    try {
      await authService.requestPasswordReset(email)
    } catch (e) {
      error.value = e instanceof Error ? e.message : t('errors.requestPasswordReset')
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function confirmPasswordReset(email: string, code: string, newPassword: string) {
    isLoading.value = true
    error.value = null

    try {
      await authService.confirmPasswordReset(email, code, newPassword)
    } catch (e) {
      error.value = e instanceof Error ? e.message : t('errors.resetPassword')
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function updateProfile(data: Partial<Pick<User, 'name' | 'surname' | 'phone' | 'language'>>) {
    if (!user.value) throw new Error(t('errors.noActiveSession'))

    isLoading.value = true
    error.value = null

    try {
      const updatedUser = await authService.updateProfile(user.value.id, data)
      user.value = {
        ...updatedUser,
        globalRoles: user.value.globalRoles,
      }
      // Only re-apply the locale on a language save: if it fails we never get
      // here, so the previously active language stays on screen.
      if (data.language !== undefined) {
        applyUserLanguage(user.value)
      }
      return user.value
    } catch (e) {
      error.value = e instanceof Error ? e.message : t('errors.updateProfile')
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function saveBankAccount(
    _data: Omit<BankAccount, 'id' | 'isVerified' | 'createdAt' | 'updatedAt'>
  ) {
    throw new Error(t('errors.bankAccountRemoved'))
  }

  function clearPendingRegistration() {
    pendingRegistration.value = null
  }

  function clearError() {
    error.value = null
  }

  return {
    user,
    pharmacy,
    bankAccount,
    token,
    isLoading,
    error,
    isInitialized,
    pendingRegistration,

    isAuthenticated,
    isOwner,
    isPharmacist,
    isCampaignAdmin,
    isReviewer,
    isReviewerL2,
    userFullName,

    initialize,
    login,
    register,
    logout,
    requestPasswordReset,
    confirmPasswordReset,
    updateProfile,
    saveBankAccount,
    clearPendingRegistration,
    clearError,
  }
})
